
import { t } from '@/i18n'
import { normalizeSiteRuntime } from '@/utils/siteRuntime'

const NORMAL_REFUND_STATES = ['未申请', '拒绝退']
const REQUESTED_REFUND_STATES = ['已申请', '已同意', '主动退', '程序退']

/** 获取当前飞机所属商铺可分配的配送订单，供任务面板和数量徽标复用。 */
export function getAssignableOrdersForPlane (orders, plane) {
  if (!plane) return []
  return (Array.isArray(orders) ? orders : []).filter(order =>
    String(order.shop_id) === String(plane.shop_id) &&
    order.shipment_status === '已接单' &&
    NORMAL_REFUND_STATES.includes(order.refund_status)
  )
}

/**
 * 汇总配送任务在提交前和执行中的风险，供下拉框、风险标签和前置检查共用。
 * blocking 表示必须阻止任务；requiresConfirmation 表示允许飞手确认后继续。
 */
export function getDeliveryOrderRisks (order, plane, options = {}) {
  if (!order || !plane) return []

  const risks = []
  const executing = Boolean(options.executing)
  const refundStatus = String(order.refund_status || '')

  if (refundStatus === '已申请') {
    risks.push({
      key: 'refund-requested',
      label: '已申请退款',
      shortLabel: '退款申请',
      tone: 'danger',
      blocking: true,
      message: t('此订单已申请退款，暂不可执行任务！')
    })
  } else if (['已同意', '主动退', '程序退'].includes(refundStatus)) {
    risks.push({
      key: 'refund-processing',
      label: '退款处理中',
      shortLabel: '退款风险',
      tone: 'danger',
      blocking: true,
      message: t('此订单正在退款或已取消，暂不可执行任务！')
    })
  }

  const runtime = normalizeSiteRuntime(order)
  if (runtime.bindRouteArray.length === 0) {
    risks.push({
      key: 'route-missing',
      label: '站点未绑定航线',
      shortLabel: '未绑航线',
      tone: 'danger',
      blocking: true,
      message: t('此站点未绑定任务航点')
    })
  }

  const orderWeight = Number(order.total_weight)
  const weightLimit = Number(plane.weight_max)
  if (Number.isFinite(orderWeight) && Number.isFinite(weightLimit) && orderWeight >= weightLimit) {
    risks.push({
      key: 'overweight',
      label: orderWeight > weightLimit ? '超出飞机载重上限' : '已达飞机载重上限',
      shortLabel: '载重风险',
      tone: 'danger',
      requiresConfirmation: true,
      confirmMessage: '此订单总重达到或超出本飞机的载重上限'
    })
  }

  const runningPlaneIds = runtime.runingArray
    .map(item => String(item || '').trim())
    .filter(Boolean)
  const conflictingPlaneIds = executing
    ? runningPlaneIds.filter(id => String(id) !== String(plane.id))
    : runningPlaneIds

  if (conflictingPlaneIds.length > 0) {
    risks.push({
      key: 'site-busy',
      label: '此站点当前已有其他飞机的配送任务。如需继续执行，请先确认现场及航线安全，并与其他飞机保持安全间距。',
      shortLabel: '同站点有其他任务，注意安全间距',
      tone: 'warning',
      requiresConfirmation: true,
      confirmMessage: '此站点当前已有其他飞机的配送任务。如需继续执行，请先确认现场及航线安全，并与其他飞机保持安全间距。'
    })
  }

  return risks
}

function timeStringToSeconds (timeString) {
  const [hours, minutes, seconds] = String(timeString || '00:00:00').split(':').map(item => Number(item) || 0)
  return hours * 3600 + minutes * 60 + seconds
}

/** 获取商铺当前营业状态，统一处理主动打烊及跨午夜营业时间。 */
export function getShopOpenState (shop, now = new Date()) {
  if (!shop) return { isOpen: false, reason: '未获取到当前商铺状态' }
  if (String(shop.manual_closed || '0') === '1') {
    return { isOpen: false, reason: shop.manual_closed_desc || '商铺已主动打烊' }
  }

  const openingTime = shop.opening_time || '00:00:00'
  const closingTime = shop.closeing_time || '23:59:59'
  const nowSeconds = now.getHours() * 3600 + now.getMinutes() * 60 + now.getSeconds()
  const openSeconds = timeStringToSeconds(openingTime)
  const closeSeconds = timeStringToSeconds(closingTime)
  const isInBusinessTime = openSeconds <= closeSeconds
    ? nowSeconds >= openSeconds && nowSeconds <= closeSeconds
    : nowSeconds >= openSeconds || nowSeconds <= closeSeconds

  if (!isInBusinessTime) {
    return { isOpen: false, reason: `当前不在营业时间内，营业时间：${openingTime.slice(0, 5)}-${closingTime.slice(0, 5)}` }
  }
  return { isOpen: true, reason: `商铺营业中，营业时间：${openingTime.slice(0, 5)}-${closingTime.slice(0, 5)}` }
}

/** 按后台管理既有口径统一生成订单状态分组。shopId 为空时统计全部商铺。 */
export function getOrderStatusGroups (orders, shopId = '') {
  const list = Array.isArray(orders) ? orders : []
  const filterByShop = shopId !== null && shopId !== undefined && String(shopId) !== ''
  const paidOrderList = filterByShop ? list.filter(item => String(item.shop_id) === String(shopId)) : list
  const pendingList = paidOrderList.filter(item => item.main_status === '已付款' && item.shipment_status === '未接单' && NORMAL_REFUND_STATES.includes(item.refund_status))
  const processingList = paidOrderList.filter(item => item.main_status === '已付款' && item.shipment_status === '已接单' && NORMAL_REFUND_STATES.includes(item.refund_status))
  const shippedList = paidOrderList.filter(item => item.main_status === '已付款' && item.shipment_status === '已发货' && NORMAL_REFUND_STATES.includes(item.refund_status))
  const requestedList = paidOrderList.filter(item => item.main_status === '已付款' && REQUESTED_REFUND_STATES.includes(item.refund_status))

  return {
    paidOrderList,
    pendingList,
    processingList,
    shippedList,
    requestedList,
    totalCount: pendingList.length + processingList.length + shippedList.length + requestedList.length
  }
}
